<template>
  <div class="page-container" v-if="provider">
    <div class="page-title-container d-flex align-items-center justify-content-between">
      <div>
        <h1 class="page-title mb-0">
          {{ provider.business_name || provider.contact_name }}
          <span :class="`badge ml-2 ${providerTypeMeta.color}`">{{ providerTypeMeta.label }}</span>
          <span :class="`badge ml-1 ${statusMeta.color}`">{{ statusMeta.label }}</span>
        </h1>
        <div class="text-muted small mt-1">
          {{ provider.city_name }} · {{ provider.phone }}
          <span v-if="provider.dni"> · DNI {{ provider.dni }}</span>
        </div>
      </div>
      <router-link :to="{ name: 'providers-list' }" class="button button-light">Volver</router-link>
    </div>

    <div class="tabs-bar">
      <button
        v-for="t in tabs"
        :key="t.key"
        type="button"
        class="tab-item"
        :class="{ active: tab === t.key }"
        @click="tab = t.key"
      >
        {{ t.label }}
      </button>
    </div>

    <!-- General -->
    <b-card v-if="tab === 'general'" tag="article" no-body class="custom-card" style="max-width: 960px">
      <b-card-body class="custom-card-body p-0">
        <ProviderForm :provider="provider" @saved="load" />
      </b-card-body>
    </b-card>

    <!-- Conductores -->
    <div v-else-if="tab === 'drivers'" class="table-container">
      <div class="table-header d-flex justify-content-between align-items-center">
        <h3 class="m-0">Conductores del proveedor</h3>
        <b-button variant="none" class="button button-primary" @click="newDriver">+ Conductor</b-button>
      </div>
      <div class="table-responsive mb-0">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th>Nombre</th>
              <th class="text-center">Documento</th>
              <th class="text-center">Celular</th>
              <th class="text-center">Acceso app</th>
              <th class="text-center">Estado</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in drivers" :key="d.id" class="clickable-row" @click="editDriver(d.id)">
              <td>{{ d.full_name }}</td>
              <td class="text-center">{{ d.identifier_number || "-" }}</td>
              <td class="text-center">{{ d.phone_number || "-" }}</td>
              <td class="text-center">
                <span :class="`badge ${d.has_app_access ? 'badge-green' : 'badge-gray'}`">
                  {{ d.has_app_access ? "Sí" : "No" }}
                </span>
              </td>
              <td class="text-center">
                <span :class="`badge ${getDriverStatus(d.status).color}`">{{ getDriverStatus(d.status).label }}</span>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!drivers.length" class="table-no-results"><p class="m-0 px-3">Sin conductores registrados.</p></div>
      </div>
    </div>

    <!-- Vehículos -->
    <div v-else-if="tab === 'vehicles'" class="table-container">
      <div class="table-header d-flex justify-content-between align-items-center">
        <h3 class="m-0">Vehículos del proveedor</h3>
        <b-button variant="none" class="button button-primary" @click="newVehicle">+ Vehículo</b-button>
      </div>
      <div class="table-responsive mb-0">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th class="text-center">Placa</th>
              <th>Marca / Modelo</th>
              <th class="text-center">Color</th>
              <th class="text-center">Año</th>
              <th class="text-center">Estado</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="v in vehicles" :key="v.id" class="clickable-row" @click="editVehicle(v.id)">
              <td class="text-center font-weight-bold">{{ v.license_plate }}</td>
              <td>{{ v.brand || "-" }} {{ v.model || "" }}</td>
              <td class="text-center">{{ v.color || "-" }}</td>
              <td class="text-center">{{ v.year || "-" }}</td>
              <td class="text-center">
                <span :class="`badge ${getVehicleStatus(v.status).color}`">{{ getVehicleStatus(v.status).label }}</span>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!vehicles.length" class="table-no-results"><p class="m-0 px-3">Sin vehículos registrados.</p></div>
      </div>
    </div>

    <!-- Viajes -->
    <div v-else-if="tab === 'rides'" class="table-container">
      <div class="table-header">
        <h3 class="m-0">Viajes asignados</h3>
      </div>
      <div class="table-responsive mb-0">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th class="text-center">Ride</th>
              <th class="text-center">Fecha / Hora</th>
              <th>Ruta</th>
              <th>Conductor designado</th>
              <th class="text-center">Estado</th>
              <th class="text-center">Tarifa proveedor</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rides" :key="r.id">
              <td class="text-center font-weight-bold">{{ r.ride }}</td>
              <td class="text-center">
                <div>{{ r.fecha }}</div>
                <div class="text-muted small">{{ r.hora }}</div>
              </td>
              <td>
                <div>{{ r.origen }}</div>
                <div class="text-muted small">{{ r.destino }}</div>
              </td>
              <td>
                <template v-if="r.assigned_driver_name">
                  <div>{{ r.assigned_driver_name }}</div>
                  <div v-if="r.assigned_vehicle_plate" class="text-muted small">{{ r.assigned_vehicle_plate }}</div>
                </template>
                <span v-else class="badge badge-yellow">Falta conductor</span>
              </td>
              <td class="text-center"><span class="badge badge-gray">{{ r.estado }}</span></td>
              <td class="text-center">
                <template v-if="r.provider_fare_amount != null">
                  {{ r.provider_fare_currency || "PEN" }} {{ r.provider_fare_amount }}
                </template>
                <template v-else>-</template>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!rides.length" class="table-no-results"><p class="m-0 px-3">Sin viajes asignados.</p></div>
      </div>
      <div class="table-footer d-flex justify-content-end align-items-center" v-if="ridesTotal > ridesSize">
        <button type="button" class="button button-light mr-2" :disabled="ridesPage === 0" @click="changeRidesPage(-1)">
          Anterior
        </button>
        <button
          type="button"
          class="button button-light"
          :disabled="(ridesPage + 1) * ridesSize >= ridesTotal"
          @click="changeRidesPage(1)"
        >
          Siguiente
        </button>
      </div>
    </div>

    <!-- Documentos (DEF-ADM-019) -->
    <DocumentsPanel v-else-if="tab === 'documents'" entityType="PROVIDER" :entityId="providerId" />

    <!-- Tarifas (DEF-ADM-020) -->
    <ProviderFaresPanel v-else-if="tab === 'fares'" :providerId="providerId" />

    <!-- Liquidaciones (DEF-ADM-021) -->
    <div v-else-if="tab === 'settlements'" class="table-container">
      <div class="table-header d-flex justify-content-between align-items-center">
        <h3 class="m-0">Liquidaciones del proveedor</h3>
        <router-link :to="{ name: 'settlements-list' }" class="button button-light">Ir a Liquidaciones</router-link>
      </div>
      <div class="table-responsive mb-0">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th>Código</th>
              <th class="text-center">Período</th>
              <th class="text-center">Viajes</th>
              <th class="text-center">Total</th>
              <th class="text-center">Estado</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="s in settlements" :key="s.id" class="clickable-row"
              @click="$router.push({ name: 'settlements-detail', params: { id: s.id } })">
              <td class="font-weight-bold">{{ s.settlement_code }}</td>
              <td class="text-center">
                <div class="small">{{ s.period_start }}</div>
                <div class="small text-muted">al {{ s.period_end }}</div>
              </td>
              <td class="text-center">{{ s.total_trips }}</td>
              <td class="text-center font-weight-bold">{{ s.currency }} {{ s.final_amount }}</td>
              <td class="text-center">
                <span :class="`badge ${settlementStatus(s.settlement_status).color}`">
                  {{ settlementStatus(s.settlement_status).label }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!settlements.length" class="table-no-results">
          <p class="m-0 px-3">Sin liquidaciones registradas.</p>
        </div>
      </div>
    </div>

    <!-- Tabs de DEFs futuros -->
    <div v-else class="table-container">
      <div class="coming-soon">
        <i class="ri-time-line"></i>
        <p class="m-0">Próximamente</p>
      </div>
    </div>
  </div>
</template>

<script>
import { Alert, Util } from "wize-admin";

import { ProviderService, DriverService, VehicleService, SettlementService } from "@/core/services";
import { DocumentsPanel } from "@/core/components";
import { Constants as LocalConstants } from "@/core/utils";
import ProviderForm from "../Components/ProviderForm.vue";
import ProviderFaresPanel from "../Components/ProviderFaresPanel.vue";

// Detalle del proveedor con tabs (DEF-ADM-018 §4.4). Documentos llega con el
// DEF-019, Tarifas con el DEF-020 y Liquidaciones con el DEF-021.
export default {
  name: "ProviderDetail",
  components: { ProviderForm, DocumentsPanel, ProviderFaresPanel },
  data() {
    return {
      provider: null,
      tab: "general",
      tabs: [
        { key: "general", label: "General" },
        { key: "drivers", label: "Conductores" },
        { key: "vehicles", label: "Vehículos" },
        { key: "documents", label: "Documentos" },
        { key: "fares", label: "Tarifas" },
        { key: "rides", label: "Viajes" },
        { key: "settlements", label: "Liquidaciones" },
      ],
      drivers: [],
      vehicles: [],
      rides: [],
      settlements: [],
      ridesPage: 0,
      ridesSize: 10,
      ridesTotal: 0,
    };
  },
  computed: {
    providerId() {
      return this.$route.params.id;
    },
    providerTypeMeta() {
      return Util.searchValue(this.provider.provider_type, LocalConstants.ProviderType);
    },
    statusMeta() {
      return Util.searchValue(parseInt(this.provider.status), LocalConstants.ProviderStatus);
    },
  },
  watch: {
    tab(val) {
      if (val === "drivers") this.loadDrivers();
      if (val === "vehicles") this.loadVehicles();
      if (val === "rides") this.loadRides();
      if (val === "settlements") this.loadSettlements();
    },
  },
  methods: {
    getDriverStatus(key) {
      return Util.searchValue(parseInt(key), LocalConstants.DriverStatus);
    },
    getVehicleStatus(key) {
      return Util.searchValue(parseInt(key), LocalConstants.VehicleStatus);
    },
    async load() {
      try {
        await this.$store.dispatch("app/loading", true);
        const res = await ProviderService.get(this.providerId);
        this.provider = res.payload;
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudo cargar el proveedor");
        this.$router.push({ name: "providers-list" });
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    async loadDrivers() {
      try {
        const res = await DriverService.list(
          { provider_id: Number(this.providerId) },
          { page: 0, size: 100 }
        );
        this.drivers = res.payload.items || [];
      } catch (ex) {
        console.error(ex);
        this.drivers = [];
      }
    },
    async loadVehicles() {
      try {
        const res = await VehicleService.list(
          { provider_id: Number(this.providerId) },
          { page: 0, size: 100 }
        );
        this.vehicles = res.payload.items || [];
      } catch (ex) {
        console.error(ex);
        this.vehicles = [];
      }
    },
    settlementStatus(key) {
      return Util.searchValue(key, LocalConstants.SettlementStatus) || { label: key, color: "badge-gray" };
    },
    async loadSettlements() {
      try {
        const res = await SettlementService.list({
          provider_id: Number(this.providerId),
          page: 0,
          size: 50,
        });
        this.settlements = res.payload.items || [];
      } catch (ex) {
        console.error(ex);
        this.settlements = [];
      }
    },
    async loadRides() {
      try {
        const res = await ProviderService.rides(this.providerId, {
          page: this.ridesPage,
          size: this.ridesSize,
        });
        this.rides = res.payload.items || [];
        this.ridesTotal = res.payload.metadata ? res.payload.metadata.totalElements : 0;
      } catch (ex) {
        console.error(ex);
        this.rides = [];
      }
    },
    changeRidesPage(delta) {
      this.ridesPage += delta;
      this.loadRides();
    },
    newDriver() {
      this.$router.push({
        name: "config-driver-create",
        query: { provider_id: this.providerId, driver_type: this.driverTypeForProvider() },
      });
    },
    editDriver(id) {
      this.$router.push({ name: "config-drivers-edit", params: { id } });
    },
    newVehicle() {
      this.$router.push({
        name: "config-vehicles-create",
        query: { provider_id: this.providerId },
      });
    },
    editVehicle(id) {
      this.$router.push({ name: "config-vehicles-edit", params: { id } });
    },
    driverTypeForProvider() {
      return this.provider.provider_type === LocalConstants.ProviderType.ASOCIADO.key
        ? LocalConstants.DriverType.ASOCIADO.key
        : LocalConstants.DriverType.TRANSPORTISTA_DRIVER.key;
    },
  },
  async mounted() {
    await this.load();
  },
};
</script>

<style lang="stylus" scoped>
.tabs-bar
  display flex
  gap 4px
  flex-wrap wrap
  border-bottom 1px solid #e5e7eb
  margin-bottom 16px
.tab-item
  background none
  border none
  border-bottom 2px solid transparent
  padding 8px 14px
  font-weight 500
  color #6c757d
  cursor pointer
  &.active
    color #007bff
    border-bottom-color #007bff
.clickable-row
  cursor pointer
.coming-soon
  display flex
  flex-direction column
  align-items center
  justify-content center
  gap 8px
  padding 48px 0
  color #6c757d
  i
    font-size 28px
</style>
